// @ts-nocheck
import { useState, useEffect } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useLocation, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { listCustomers } from '@/api/endpoints/customers';
import { useDialer } from '@/contexts/DialerContext';
import { GeneralSection } from './settings/GeneralSection';
import { NumbersSection } from './settings/NumbersSection';
import { IncomingCallsSection } from './settings/IncomingCallsSection';
import { IntegrationsSection } from './settings/IntegrationsSection';
import { AISection } from './settings/AISection';
import { BlacklistSection } from './settings/BlacklistSection';
import { ApiKeysSection } from './settings/ApiKeysSection';
import { WebhooksSection } from './settings/WebhooksSection';
import { resolveSettingsSection, settingsGroupOf } from './settings/sections';
import { useAuthStore } from '@/stores/authStore';

/** Sections whose controls save as soon as they change (QA-W7-3). */
const SAVE_ON_CHANGE = new Set(['general', 'incoming', 'ai']);

// ─── Root component ───────────────────────────────────────────────────────────

export default function SettingsScreen() {
  const { t } = useTranslation(['settings', 'common', 'dialer']);
  const d = useDialer();
  const readOnly = useAuthStore((st) => !!st.impersonation);
  // #50 -- la section active vient de l'URL (?section=...) quand elle y est :
  // le menu de gauche peut ainsi pointer directement sur une sous-section, et
  // l'adresse d'une section devient partageable.
  // W4: an old or unknown value is resolved (?section=coaching opens IA,
  // where coaching now lives); the effect below then rewrites the URL.
  const [activeSection, setActiveSection] = useState(() => {
    try {
      return resolveSettingsSection(new URLSearchParams(window.location.search).get('section'));
    } catch { return resolveSettingsSection(null); }
  });

  // On garde l'URL alignee sur l'onglet ouvert, sans empiler d'historique.
  useEffect(() => {
    try {
      const u = new URL(window.location.href);
      if (u.searchParams.get('section') !== activeSection) {
        u.searchParams.set('section', activeSection);
        window.history.replaceState(window.history.state, '', u.toString()); // keep React Router's state (key, idx)
        // Lets the left menu highlight the section opened by default.
        window.dispatchEvent(new Event('xdial-settings-section'));
      }
    } catch { /* URL indisponible */ }
  }, [activeSection]);

  // Le menu de gauche change l'URL sans remonter le composant : on l'ecoute.
  useEffect(() => {
    const relire = () => {
      try {
        const p = new URLSearchParams(window.location.search).get('section');
        if (p) setActiveSection(resolveSettingsSection(p));
      } catch { /* URL indisponible */ }
    };
    window.addEventListener('xdial-settings-section', relire);
    return () => window.removeEventListener('xdial-settings-section', relire);
  }, []);

  // REV-W4-F3: a router navigation to ?section=... while the screen is
  // mounted (« Obtenir un numéro » in Appels entrants) used to change the URL
  // and nothing else. Follow the router's search params too; keyed on
  // location.key so a navigation back to a section the menu had switched
  // away from (by replaceState, unseen by the router) still applies. The
  // event above stays for the left menus, and is sent so they highlight it.
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const urlSection = searchParams.get('section');
  useEffect(() => {
    if (!urlSection) return;
    setActiveSection(resolveSettingsSection(urlSection));
    window.dispatchEvent(new Event('xdial-settings-section'));
  }, [urlSection, location.key]);

  // ADR-031 — IntegrationsSection operates on Customers (per-Customer CRM).
  // The legacy `allClients` (Client.list) has been removed; Customers come
  // from the new tenant_customer_links registry exposed by /api/customers.
  const { data: customers = [] } = useQuery({
    queryKey: ['customers', { activeOnly: true }],
    queryFn: ({ signal }) => listCustomers({ activeOnly: true, signal }),
    enabled: !!d.callerData,
    staleTime: 60_000,
  });

  return (
    /* 🚨 Aucune hauteur imposee et aucun defilement ici : la mise en page du
       dialer porte deja le conteneur qui defile autour de l'Outlet. En ajouter
       un second donnait deux ascenseurs imbriques (Kevin, 2026-09-30). */
    /* No entrance animation (DES-20: motion only for a change of state). */
    <div className="flex flex-col">
      {/* No tab bar here: the left menu lists the sections under Settings,
          and showing both navigations at once was redundant. */}
      <div className="min-w-0 px-6 py-6">
        {/* W4 (B2-settings, F6): each section says where you are and what it
            sets, under the group it belongs to (Mon compte / Organisation). */}
        <header className="mb-6 max-w-3xl" data-testid="settings-section-header">
          <p className="text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
            {t(`settings:groups.${settingsGroupOf(activeSection)}`)}
          </p>
          <h2 className="text-lg font-semibold text-foreground mt-0.5">{t(`settings:sections.${activeSection}`)}</h2>
          <p className="text-sm text-muted-foreground mt-1">{t(`settings:sectionDescriptions.${activeSection}`)}</p>
        </header>
        {/* QA-W7-3: « ouvrir en tant que » is read-only. Say so on every
            section; the sections whose controls save on change (Général,
            Appels entrants, IA) are disabled, the others keep their reading
            controls and the server's refusal is translated. */}
        {readOnly && (
          <p role="note" data-testid="settings-read-only" className="mb-4 max-w-3xl rounded-md border px-3 py-2 text-sm" style={{ borderColor: 'var(--vf-border)', background: 'var(--vf-surface-2)', color: 'var(--vf-text-2)' }}>
            {t('common:permissionGuard.readOnly')}
          </p>
        )}
        <fieldset disabled={readOnly && SAVE_ON_CHANGE.has(activeSection)} className="min-w-0 border-0 p-0 m-0">
        {activeSection === 'general' && (
          <GeneralSection d={d} />
        )}
        {activeSection === 'numbers' && (
          <NumbersSection d={d} />
        )}
        {activeSection === 'incoming' && (
          <IncomingCallsSection d={d} />
        )}
        {activeSection === 'integrations' && (
          <IntegrationsSection d={d} customers={customers} />
        )}
        {activeSection === 'ai' && (
          <AISection />
        )}
        {activeSection === 'blacklist' && (
          <BlacklistSection d={d} />
        )}
        {activeSection === 'apiKeys' && (
          <ApiKeysSection />
        )}
        {activeSection === 'webhooks' && (
          <WebhooksSection />
        )}
        </fieldset>
      </div>
    </div>
  );
}
